// Permission-gate detection. Three independent signals:
//
//   1. Rendered gate signature — the PRIMARY, paint-time trigger
//      (detectGateScreen): gate footer + numbered Yes/No options in the
//      rendered screen. Fires within one scrape throttle tick of the paint.
//
//   2. OSC 777 escape — the deterministic FALLBACK trigger. The PTY emits
//      `\x1b]777;notify;Claude Code;Claude needs your permission\x07`
//      (often tmux-wrapped: `\x1bPtmux;\x1b\x1b]777;notify;…\x1b\`) — but
//      ~6s AFTER the gate paints (measured 2026-08-08, see
//      docs/superpowers/specs/2026-08-08-paint-time-gate-detection-design.md).
//      Still load-bearing: it covers a gate whose options never painted, and
//      its "waiting for your input" body is the authoritative close signal.
//
//   3. Rendered option scrape — the payload builder. The gate's numbered
//      options are painted via absolute-cursor moves, so they live in the
//      rendered headless buffer (getOutputLines), not the raw byte stream. We
//      read the ACTUAL leading numbers and the `❯` cursor — the numbers are
//      NOT a stable 1-based index (a gate can show "2. Yes / 3. No"), which
//      is exactly the "2 didn't take" bug this avoids. The scrape takes the
//      LAST option block scanning bottom-up, since a numbered list in Claude's
//      prose above the gate can match the same option shape and must not be
//      swept in.

export interface PermissionOption {
  /** The real leading number shown on screen (NOT a 1-based array index). */
  index: number;
  label: string;
  /**
   * Literal keystroke bytes that answer this option, when the number alone
   * isn't the answer (e.g. a y/N shell prompt answers "y\r"/"n\r", not "1\r").
   * Additive: OSC-777 gates omit it and the client answers via `index`; the
   * unstructured shell-prompt path (detectShellPrompt) populates it.
   */
  answerKeys?: string;
}

export interface PermissionGate {
  /** Prompt text above the options, e.g. "Claude needs your permission to use Bash". */
  prompt?: string;
  /**
   * The descriptive block above the prompt — the tool title, the command, and any
   * action description Claude paints inside the gate box (newline-joined). Lets the
   * client show WHAT is being permitted, not just "Do you want to proceed?".
   */
  detail?: string;
  options: PermissionOption[];
  /** Index value (the on-screen number) of the `❯`-highlighted option, if any. */
  cursor?: number;
}

/**
 * Stable content key for de-duping repeated broadcasts of the same gate across
 * PTY repaint ticks. Includes cursor — moving the on-screen selection is a
 * real update the client needs, so it must produce a new key.
 */
export function permissionContentKey(gate: {
  prompt?: string;
  detail?: string;
  options: PermissionOption[];
  cursor?: number;
}): string {
  return `${gate.prompt ?? ""}::${gate.detail ?? ""}::${gate.options
    .map((o) => `${o.index}.${o.label}`)
    .join(",")}::${gate.cursor ?? ""}`;
}

/**
 * The gate's IDENTITY key: the content key with the cursor stripped.
 *
 * Cursor belongs in the broadcast dedupe key (moving the highlight is a real
 * update) but never in identity — the same gate with a different highlight is
 * the same gate. `closedGateKey` in pty-manager.ts already draws exactly this
 * line; this names it so the answer route and the client agree on one spelling.
 *
 * Content-derived, not instance-derived: it distinguishes gates with different
 * prompt/detail/options, NOT two consecutive gates whose content is identical.
 */
export function permissionGateKey(gate: {
  prompt?: string;
  detail?: string;
  options: PermissionOption[];
}): string {
  return permissionContentKey({ ...gate, cursor: undefined });
}

// OSC 777 with the notify command from Claude Code. We match the stable core
// (`]777;notify;Claude Code`) rather than the whole tmux-passthrough wrapper so
// detection survives both the wrapped and unwrapped forms.
//
// The BODY is load-bearing: Claude Code emits OSC 777 for two different events,
// and only one of them is a gate.
//
//   "Claude needs your permission"      → a real gate; options paint later.
//   "Claude is waiting for your input"  → the turn ENDED; there is no gate.
//
// Matching the prefix alone conflated the two, so an end-of-turn notify opened a
// permission card that could never populate or close: scrapePermissionGate finds
// no options (correctly — there are none), the caller broadcasts `{options: []}`,
// and the recovery path needs a prompt marker from a LATER chunk that never
// arrives because the turn is over. Result: a stuck "Claude needs your
// permission" card with only Cancel. Anchor on the permission body instead.
// biome-ignore lint/suspicious/noControlCharactersInRegex: matching the literal OSC 777 escape
const OSC_777_PERMISSION_RE = /\x1b\]777;notify;Claude Code;[^\x07\x1b]*needs your permission/;

// biome-ignore lint/suspicious/noControlCharactersInRegex: matching the literal OSC 777 escape
const OSC_777_WAITING_RE = /\x1b\]777;notify;Claude Code;[^\x07\x1b]*waiting for your input/;

/** True if a raw PTY chunk contains the Claude Code OSC 777 permission notify. */
export function hasPermissionOsc(rawData: string): boolean {
  return OSC_777_PERMISSION_RE.test(rawData);
}

/**
 * True if a raw PTY chunk contains the OSC 777 "waiting for your input" notify —
 * Claude finished its turn. Not a gate: it is the positive signal that any gate
 * we were tracking is over, which is what lets a stuck card close without
 * waiting for a prompt marker that will never come.
 */
export function hasWaitingForInputOsc(rawData: string): boolean {
  return OSC_777_WAITING_RE.test(rawData);
}

// A rendered option row: optional cursor, then "N. label". Leading spaces
// from the box/indent are tolerated (the box gutter is stripped first). We
// capture N and the label separately.
//
// The cursor may be `❯`, `›` or ASCII `>`. Claude Code paints it as a plain `>`
// (U+003E) on Windows — measured from a real gate's PTY bytes, which render the
// highlighted row as " > 1. Yes" against "   2. No" for the rest. Accepting only
// `❯` dropped that row from the options entirely, and the prompt search below
// then picked it up as the question — so the plain "Yes" never reached the
// client and "switch to auto mode" was left as the only way to approve.
// PROMPT_ARROW_RE below and codexScreen.ts already accept all three.
const OPTION_RE = /^\s*([❯›>])?\s*(\d+)\.\s+(.+?)\s*$/;

// Chrome lines that are never the prompt: footers, box-drawing, prompt arrows.
const FOOTER_RE = /Enter to select|Esc to cancel|↑|↓|to navigate|to cancel/i;
const BOX_ONLY_RE = /^[\s│─┌┐└┘├┤┬┴┼╭╮╰╯╱╲=_-]+$/;
const PROMPT_ARROW_RE = /^[\s]*[❯›>]\s*$/;

// Strip leading and trailing box-drawing gutters ("│ … │") so a boxed gate
// frame parses the same as an unboxed one.
function stripGutter(line: string): string {
  return line.replace(/^\s*[│|]\s?/, "").replace(/\s*[│|]\s*$/, "");
}

/**
 * Scrape the permission gate's options + prompt from rendered screen lines.
 * Scrapes the LAST option block on screen, scanning bottom-up: a stale
 * numbered list sitting in scrollback above the real gate (e.g. Claude's own
 * prose) matches the same option shape and must not be vacuumed in with it.
 * Returns null when no numbered options are present (not a gate, or not painted
 * yet). Pure — no I/O.
 */
export function scrapePermissionGate(lines: string[]): PermissionGate | null {
  const options: PermissionOption[] = [];
  let cursor: number | undefined;
  let cursorRows = 0;
  let firstOptionLine = -1;

  for (let i = lines.length - 1; i >= 0; i--) {
    const stripped = stripGutter(lines[i]);
    const m = OPTION_RE.exec(stripped);
    if (m) {
      const index = Number.parseInt(m[2], 10);
      if (!Number.isFinite(index)) continue;
      firstOptionLine = i; // overwritten as we walk up — ends up topmost
      if (m[1]) {
        cursor = index; // the glyph marks the highlighted option
        cursorRows++;
      }
      options.unshift({ index, label: m[3] }); // keep top-to-bottom screen order
      continue;
    }
    if (options.length === 0) continue; // still below the block: footer, box bottom, blanks
    // Inside the block: a blank line or the box edge marks its top boundary.
    if (stripped.trim().length === 0 || BOX_ONLY_RE.test(lines[i].trim())) break;
    // otherwise: a wrapped option label or the prompt line — keep walking
  }

  if (options.length === 0) return null;

  // A selection cursor marks exactly ONE row; a line PREFIX marks every row.
  // Markdown blockquotes are the case that matters — `> 1. Rebase` / `> 2. Squash`
  // in Claude's prose is option-shaped and carries a `>` on both rows — but the
  // rule is about the shape, not the glyph, so a quoted `❯` list is caught too.
  // Reporting a highlight here would be wrong data on its own, and it is what
  // let detectPickerScreen claim such a block whenever no composer rule
  // happened to sit below it. The two guards are complementary: this one
  // rejects quoted prose (every row marked), the composer-rule test rejects
  // typed or pasted text (one row marked, by the composer's own `❯` prompt).
  if (cursorRows > 1) cursor = undefined;

  // Prompt = nearest non-empty, non-chrome line above the first option row.
  let prompt: string | undefined;
  let promptLine = -1;
  for (let i = firstOptionLine - 1; i >= 0; i--) {
    const t = stripGutter(lines[i]).trim();
    if (t.length === 0) continue;
    if (BOX_ONLY_RE.test(lines[i].trim()) || FOOTER_RE.test(t) || PROMPT_ARROW_RE.test(t)) continue;
    prompt = t || undefined;
    promptLine = i;
    break;
  }

  // Detail = the descriptive block ABOVE the prompt (tool title + command +
  // action description), newline-joined. The box top, 2+ consecutive blanks (the
  // gap to prior scrollback), or a ~6-line cap ends the block — so a
  // partial-window scrape can't vacuum unrelated terminal output.
  const detail = promptLine > 0 ? scrapeDetail(lines, promptLine) : undefined;

  return {
    ...(prompt ? { prompt } : {}),
    ...(detail ? { detail } : {}),
    options,
    ...(cursor !== undefined ? { cursor } : {}),
  };
}

const MAX_DETAIL_LINES = 6;

function scrapeDetail(lines: string[], promptLine: number): string | undefined {
  const collected: string[] = []; // bottom-up, content lines only
  let blankRun = 0;
  for (let i = promptLine - 1; i >= 0; i--) {
    // Strip the gutter first: a bare "│" is an INTERNAL blank line, not a frame
    // edge — only the true border rows ("╭──╮" / "╰──╯") are box-only once the
    // gutter is removed.
    const t = stripGutter(lines[i]).trim();
    if (BOX_ONLY_RE.test(t)) break; // box top / frame edge
    if (t.length === 0) {
      blankRun++;
      if (blankRun >= 2) break; // gap to prior scrollback
      continue; // single blank: skip (join already separates lines)
    }
    blankRun = 0;
    if (FOOTER_RE.test(t) || PROMPT_ARROW_RE.test(t)) continue; // skip chrome, keep walking
    collected.push(t);
    if (collected.length >= MAX_DETAIL_LINES) break;
  }

  return collected.length > 0 ? collected.reverse().join("\n") : undefined;
}

// ── Paint-time gate signature ──────────────────────────────────────
// The OSC 777 notify is debounced ~6s by Claude Code (measured 2026-08-08,
// see docs/superpowers/specs/2026-08-08-paint-time-gate-detection-design.md),
// so waiting for it makes the mobile card lag a fully painted gate. This
// classifier claims a gate from the RENDERED screen alone. All three anchors
// must hold — the Yes/No label test is what keeps a numbered list in Claude's
// prose from opening a card.

// Gate footer ("Esc to cancel · Tab to amend · ctrl+e to explain"); "esc to
// cancel" is the stable core across versions.
const GATE_FOOTER_RE = /esc to cancel/i;
// AskUserQuestion footer — that path has priority (its footer also contains
// "Esc to cancel", so this must be tested first).
const ASK_MENU_FOOTER_RE = /Enter to select/i;
// Same family test detectQuestionFromScreen uses in reverse to REJECT gates.
const YES_NO_LABEL_RE = /^(yes|no)\b/i;

/**
 * Claim a permission gate from rendered screen lines without an OSC 777.
 * Returns the scraped gate when the full gate signature is present:
 * ≥2 numbered options, the gate footer, no Ask-menu footer, and at least one
 * Yes/No-family option label. Pure — no I/O.
 */
export function detectGateScreen(lines: string[]): PermissionGate | null {
  if (lines.some((l) => ASK_MENU_FOOTER_RE.test(l))) return null;
  if (!lines.some((l) => GATE_FOOTER_RE.test(l))) return null;
  const gate = scrapePermissionGate(lines);
  if (!gate || gate.options.length < 2) return null;
  if (!gate.options.some((o) => YES_NO_LABEL_RE.test(o.label))) return null;
  return gate;
}

// Claude Code's composer draws its "❯" prompt between two full-width ─ rules.
// A live picker replaces the composer, so a rule below a numbered block means
// the block is not a picker the user is being asked to answer.
const COMPOSER_RULE_RE = /^\s*─{8,}\s*$/;

/**
 * Claim an unboxed numbered picker from rendered screen lines — one that prints
 * no gate footer, so detectGateScreen never sees it (Claude Code's first-run
 * theme picker, which follows its options with a preview and a "Syntax theme"
 * line; #863). The ❯ selection cursor is what separates a live picker from a
 * numbered list in prose (#821). It is not enough on its own: ❯ is also the
 * composer's prompt glyph, so typed or pasted "1. …" text renders as a
 * cursor-marked numbered block between the composer's rules, and an answered
 * menu stays painted above a live composer (#724). Both leave a composer rule
 * below the cursor row; a live picker does not. Boxed blocks stay
 * detectGateScreen's. Pure — no I/O.
 */
export function detectPickerScreen(lines: string[]): PermissionGate | null {
  if (lines.some((l) => ASK_MENU_FOOTER_RE.test(l))) return null;
  const gate = scrapePermissionGate(lines);
  if (!gate || gate.cursor === undefined || gate.options.length < 2) return null;
  let cursorRow = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    const m = OPTION_RE.exec(stripGutter(lines[i]));
    if (m?.[1] && Number.parseInt(m[2], 10) === gate.cursor) {
      cursorRow = i;
      break;
    }
  }
  if (cursorRow < 0 || /^\s*│/.test(lines[cursorRow])) return null;
  for (let i = cursorRow + 1; i < lines.length; i++) {
    if (COMPOSER_RULE_RE.test(lines[i])) return null;
  }
  return gate;
}
